/**
 * Error Message Utilities
 * Standardized error messages with suggested actions
 */

export interface ErrorWithAction {
  message: string;
  suggestedAction?: string;
  technicalDetails?: string;
}

export const ErrorMessages = {
  networkError: (action: string): ErrorWithAction => ({
    message: `Unable to ${action} due to network issues`,
    suggestedAction: 'Check your internet connection and try again.',
  }),

  unauthorized: (resource: string): ErrorWithAction => ({
    message: `You don't have permission to access ${resource}`,
    suggestedAction: 'Contact your administrator if you need access to this resource.',
  }),

  requiredFields: (fields: string[]): ErrorWithAction => ({
    message: 'Please fill in all required fields',
    suggestedAction: `Missing: ${fields.join(', ')}`,
  }),

  invalidFormat: (field: string, format: string): ErrorWithAction => ({
    message: `Invalid ${field} format`,
    suggestedAction: `Please enter ${field} in format: ${format}`,
  }),

  notFound: (resource: string): ErrorWithAction => ({
    message: `${resource} not found`,
    suggestedAction: 'The requested item may have been deleted. Please refresh and try again.',
  }),

  alreadyExists: (resource: string): ErrorWithAction => ({
    message: `This ${resource} already exists`,
    suggestedAction: 'Please check if you meant to update the existing item instead.',
  }),

  hasDependencies: (resource: string, dependencies: string[]): ErrorWithAction => ({
    message: `Cannot delete ${resource} - it has related items`,
    suggestedAction: `Remove these first: ${dependencies.join(', ')}`,
  }),

  integrationNotConfigured: (service: string): ErrorWithAction => ({
    message: `${service} is not configured`,
    suggestedAction: 'Go to Settings > Integrations to configure this service.',
  }),

  integrationFailed: (service: string, action: string): ErrorWithAction => ({
    message: `${service} failed to ${action}`,
    suggestedAction: 'Check integration settings and try again. Contact support if the problem persists.',
  }),

  workflowFailed: (workflow: string, step: string): ErrorWithAction => ({
    message: `${workflow} workflow failed at step: ${step}`,
    suggestedAction: 'Please complete this step manually or contact support.',
  }),

  insufficientFunds: (account: string): ErrorWithAction => ({
    message: 'Insufficient funds in account',
    suggestedAction: `Check ${account} balance and ensure sufficient funds before processing.`,
  }),

  unbalancedEntry: (): ErrorWithAction => ({
    message: 'Journal entry is not balanced',
    suggestedAction: 'Ensure total debits equal total credits (double-entry accounting).',
  }),

  unknown: (error?: string): ErrorWithAction => ({
    message: 'An unexpected error occurred',
    suggestedAction: 'Please try again. If the problem persists, contact support.',
    technicalDetails: error,
  }),
};

export function formatErrorMessage(error: Error | ErrorWithAction | string): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  let message = error.message;
  if (error.suggestedAction) {
    message += ` ${error.suggestedAction}`;
  }
  if (error.technicalDetails) {
    message += ` (${error.technicalDetails})`;
  }

  return message;
}

/**
 * Duck-typed RPC API failure from the web `RpcApiError` class (`name === 'RpcApiError'`).
 * Shared-utils cannot import app transport; keep fields aligned with `apps/web/src/services/api/transport.ts`.
 */
export interface RpcApiErrorLike extends Error {
  readonly code?: string;
  readonly details?: Record<string, unknown>;
  readonly provider?: string;
  readonly retryable?: boolean;
  readonly providerCode?: string;
  readonly field?: string;
  readonly requestId?: string;
}

export function isRpcApiErrorLike(error: unknown): error is RpcApiErrorLike {
  return error instanceof Error && error.name === 'RpcApiError';
}

function isIntegrationRpcErrorCode(code: string | undefined): boolean {
  return code?.startsWith('INTEGRATION_') ?? false;
}

function errorWithActionFromRpcApiError(error: RpcApiErrorLike): ErrorWithAction {
  const code = error.code;

  if (code === 'TRUST_ACCOUNT_MISSING' || code === 'TRUST_ACCOUNT_TYPE') {
    return {
      message: error.message,
      suggestedAction:
        'Link a Trust bank account to this property in bank account settings before security deposit flows.',
    };
  }

  if (code === 'VALIDATION_FAILED' || code === 'DOMAIN_VALIDATION_FAILED') {
    return {
      message: error.message,
      suggestedAction: error.field
        ? `Check the highlighted field (${error.field}).`
        : 'Review the form and correct any invalid values.',
    };
  }

  if (code === 'FORBIDDEN') {
    return ErrorMessages.unauthorized('this resource');
  }

  if (code === 'INTERNAL_ERROR') {
    return ErrorMessages.unknown(error.message);
  }

  if (isIntegrationRpcErrorCode(code)) {
    const label = error.provider ?? 'Integration';
    const base = ErrorMessages.integrationFailed(label, 'complete this request');
    const retryHint =
      error.retryable === true ? ' You can try again in a moment.' : '';
    return {
      message: error.message || base.message,
      suggestedAction: `${base.suggestedAction ?? ''}${retryHint}`.trim(),
      technicalDetails: error.providerCode,
    };
  }

  return {
    message: error.message,
    suggestedAction: 'Please try again or contact support if the problem persists.',
  };
}

export function getErrorWithAction(error: unknown): ErrorWithAction {
  if (typeof error === 'string') {
    return { message: error };
  }

  if (isRpcApiErrorLike(error)) {
    return errorWithActionFromRpcApiError(error);
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();

    if (msg.includes('not found or unauthorized') || (msg.includes('unauthorized') && msg.includes('not found'))) {
      return {
        message: "You don't have permission to access this resource, or it was not found.",
        suggestedAction: 'Please refresh and try again. Contact your administrator if you need access.',
      };
    }

    if (msg.includes('not found')) {
      return ErrorMessages.notFound(error.message.split(' ')[0] || 'Resource');
    }

    if (msg.includes('unauthorized') || msg.includes('permission')) {
      return ErrorMessages.unauthorized('this resource');
    }

    if (msg.includes('network') || msg.includes('fetch')) {
      return ErrorMessages.networkError('complete this action');
    }

    if (msg.includes('already exists') || msg.includes('duplicate')) {
      return ErrorMessages.alreadyExists('item');
    }

    return {
      message: error.message,
      suggestedAction: 'Please try again or contact support if the problem persists.',
    };
  }

  return ErrorMessages.unknown(JSON.stringify(error));
}

export function getFriendlyErrorMessage(error: unknown): string {
  return formatErrorMessage(getErrorWithAction(error));
}
